/**
 * V2.0.81 — Client-side interaction-telemetry batcher.
 *
 * Collects `{tool, action, ms, committed, design_id, meta}` events from
 * the FloorPlan2D interaction hooks and POSTs them to
 * `/api/interaction-events` in bulk. Never blocks the UI:
 *
 *   - On each `emit(...)` we push into an in-memory buffer.
 *   - Every 15s (if the buffer is non-empty) we flush to the backend.
 *   - On `beforeunload` we use `navigator.sendBeacon` so the final
 *     batch survives a tab close.
 *   - Errors are swallowed — telemetry must NEVER surface a toast or
 *     interrupt a user flow.
 *
 * Import + call `emitInteractionEvent({tool, action, ...})` from any
 * hook that wants to report. `startTelemetryFlush(designId)` should be
 * called once from the Editor mount effect (it's a no-op on re-mount).
 */

const BUFFER = [];
const FLUSH_INTERVAL_MS = 15_000;
const MAX_BUFFER = 200;
let flushTimer = null;
let started = false;
let currentDesignId = null;

function endpoint() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/interaction-events`;
}

function drainAndPost(useBeacon = false) {
  if (!BUFFER.length) return;
  const batch = BUFFER.splice(0, BUFFER.length);
  const body = JSON.stringify({ events: batch });
  const url = endpoint();
  if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    } catch (_e) {
      // fall through to fetch
    }
  }
  fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {
    // Silent — this is telemetry, not user-visible. Keep the last
    // batch that failed to send? No: it's better to drop than to keep
    // retrying and eventually block a real request behind a stale one.
  });
}

/**
 * Push a single interaction event onto the buffer. Safe to call from
 * anywhere including inside render (though prefer effect / event
 * handler for clarity).
 */
export function emitInteractionEvent(evt) {
  if (!evt || typeof evt !== "object") return;
  if (BUFFER.length >= MAX_BUFFER) {
    // Drop the oldest to make room — we care more about the freshest
    // signal than a perfect historical trace.
    BUFFER.shift();
  }
  BUFFER.push({
    tool: evt.tool || "",
    action: evt.action || "",
    ms: typeof evt.ms === "number" ? Math.max(0, Math.round(evt.ms)) : null,
    committed: typeof evt.committed === "boolean" ? evt.committed : null,
    design_id: evt.design_id || currentDesignId || null,
    meta: evt.meta || null,
  });
}

/**
 * Wire up the periodic flush + beforeunload beacon. Idempotent so it's
 * safe to call from multiple Editor mount effects.
 */
export function startTelemetryFlush(designId) {
  currentDesignId = designId || currentDesignId;
  if (started) return;
  started = true;
  flushTimer = setInterval(() => drainAndPost(false), FLUSH_INTERVAL_MS);
  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", () => drainAndPost(true));
    // Fire on visibilitychange too so background-tab suspensions don't
    // lose the last batch (Chrome kills tabs after ~5 min in the
    // background).
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") drainAndPost(true);
    });
  }
}

/**
 * Cancel the periodic flush. Not usually needed — the batcher is
 * cheap and safe to leave running for the whole session. Exposed for
 * tests.
 */
export function stopTelemetryFlush() {
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
  started = false;
}
